import { AfterViewInit, Component, computed, inject, OnInit, output, resource, viewChild } from "@angular/core";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { captureException } from "@sentry/angular";
import { lastValueFrom } from "rxjs";
import {
    AnalyticsService,
    AuthService,
    CardService,
    CategoriesService,
    DashboardsService,
    DashboardUpdateMetadata,
    HelpersService,
    IFirmContactInformationService,
    IFirmContactInformationUpdate,
    IFirmDeliveryMethodsService,
    IFirmGroupsService,
    IFirmRolesService,
    IfirmTaxDocumentService,
    MappingOfStatusesService,
    PatchCardBatchResponse,
    ResponseErrorHandlerService,
    UnassignedUserId,
    UsersService,
    UtilsService,
    DeliveryMethodsService,
    IFirmUsersService,
    compareByFirstAndLastNames,
    compareByLocalizedNameNoneFirst,
    PARTNER_ROLE_ID,
    MANAGER_ROLE_ID,
    PREPARER_ROLE_ID,
    REVIEWER_ROLE_ID,
    ADMINISTRATION_ROLE_ID,
} from "../shared";
import { IDashboardModel, isiFirmTaxDashboard } from "../views-interfaces/dashboard-model.interface";
import { SaveResultEnum } from "../views-interfaces/save-result.enum";
import { CardDisplayService } from "../shared/card-display/card-display.service";
import { GroupResult, groupBy } from "@progress/kendo-data-query";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";
import { JobsAndBillingStatusesService } from "@app/shared/jobs-and-billing-statuses-service/jobs-and-billing-statuses.service";
import { JobsAndBillingCommonService } from "@app/shared/jobs-and-billing-common-service/jobs-and-billing-common.service";
import { IJobsCurrentSettings } from "@app/shared/interfaces/jobs-current-settings-response.interface";
import { DatePipe } from "@angular/common";
import { DatePickerComponent } from "@progress/kendo-angular-dateinputs";
import { JobsAndBillingJobsService } from "@app/shared/jobs-and-billing-jobs-service/jobs-and-billing-jobs.service";
import { JobsAndBillingJobTypesService } from "@shared/jobs-and-billing-job-types-service";
import { CardModalService } from "@app/shared/card-modal";
import { IJobsBulkJobModel } from "@app/shared/interfaces/jobs-job-bulk-modal.interface";
import { JobsAndBillingPriorityService } from "@shared/jobs-and-billing-priority";
import { ITaxReturnStatus, SupportedLanguage } from "@app/views-interfaces";

interface Option {
    value: string;
    label: string | object;
    order?: number;
    disabled?: boolean;
    groupingField?: string;
}

const ROLE_OPTIONS_ORDER: Record<string, number> = {
    MANAGER_ROLE_ID: 5,
    PARTNER_ROLE_ID: 6,
    PREPARER_ROLE_ID: 7,
    REVIEWER_ROLE_ID: 8,
    ADMINISTRATION_ROLE_ID: 9,
};

@Component({
    selector: "wk-bulkchange",
    templateUrl: "./bulk-change.component.html",
    styleUrl: "./bulk-change.component.scss",
    standalone: false,
    host: { class: "wk-bulkchange" },
})
export class BulkChangeComponent implements OnInit, AfterViewInit {
    private readonly authService = inject(AuthService);
    private readonly cardService = inject(CardService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly iFirmContactInformationService = inject(IFirmContactInformationService);
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly ifirmTaxDocumentService = inject(IfirmTaxDocumentService);
    private readonly iFirmUsersService = inject(IFirmUsersService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly responseErrorHandler = inject(ResponseErrorHandlerService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly translateService = inject(TranslateService);
    private readonly usersService = inject(UsersService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly iFirmDeliveryMethodsService = inject(IFirmDeliveryMethodsService);
    private readonly deliveryMethodsService = inject(DeliveryMethodsService);
    private readonly jobsAndBillingStatusesService = inject(JobsAndBillingStatusesService);
    private readonly jobsAndBillingCommonService = inject(JobsAndBillingCommonService);
    private readonly jobsAndBillingPriorityService = inject(JobsAndBillingPriorityService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly datePipe = inject(DatePipe);
    private readonly jobsAndBillingJobsService = inject(JobsAndBillingJobsService);
    public readonly cardModalService = inject(CardModalService);
    private readonly translate = inject(TranslateService);

    public closeModal = output<void>();

    private isiFirmTaxDashboard = false;
    protected isJobsBillingDashboard = false;
    protected currentLanguage: SupportedLanguage = "en-CA";
    private cardsToChange = {};
    protected affectedDeclarations = 0;
    protected completedUpdateCounter = 0;
    private chunkSize = 100;
    protected isUpdating = false;
    protected isDone = false;

    protected placeholderOption: Option = { value: undefined, label: "BulkChange.Select", disabled: true };
    protected returnFieldOptions: Option[] = [this.placeholderOption];
    protected groupedReturnFieldOptions: GroupResult[] = [
        {
            field: "groupingField",
            value: undefined,
            items: [this.placeholderOption],
            aggregates: {},
        },
    ];
    protected valueOptions: Map<string, Option[]>;
    protected selectedReturnField: Option = this.placeholderOption;
    protected selectedReturnFieldOptions: Option[] = [this.placeholderOption];
    protected selectedValue: Option = this.placeholderOption;
    currentDashboard: IDashboardModel | undefined;
    public groupedData: GroupResult[];
    private jobsAndBillingCurrentSettings: IJobsCurrentSettings;
    protected isMultipleJobTypesSelected = false;
    protected invalidTargetStartDate = false;
    protected invalidTargetEndDate = false;
    datepicker = viewChild<DatePickerComponent>("dp");

    constructor() {
        this.currentLanguage = (
            this.translateService.getCurrentLang() ? this.translateService.getCurrentLang() : this.translateService.getFallbackLang()
        ) as SupportedLanguage;
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang as SupportedLanguage;
            this.updateOptionsAndSelections();
        });
    }

    private updateOptionsAndSelections() {
        this.valueOptions = new Map([...this.valueOptions, ...this.updateRoleValueOptions()]);
        this.selectedReturnFieldOptions = this.valueOptions?.get(this.selectedReturnField.value) ?? [this.placeholderOption];
        this.selectedValue =
            this.valueOptions?.get(this.selectedReturnField.value)?.find((option) => option.value === this.selectedValue.value) ??
            this.placeholderOption;
    }

    async ngOnInit() {
        this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        this.isiFirmTaxDashboard = isiFirmTaxDashboard(this.currentDashboard);
        this.isJobsBillingDashboard = isJobsBilling(this.currentDashboard.ApplicationType);

        if (this.isJobsBillingDashboard) {
            await this.getJobsAndBillingCurrentSettings();
        }

        // These are needed to populate signals that rolesWithUsers depends on via usersById, otherwise custom roles options would be empty
        await this.cardDisplayService.loadAllCards();
        await this.iFirmUsersService.getUsers();

        this.analyticsService.track("Access Bulk Changes");

        this.returnFieldOptions = this.buildReturnFieldOptions();
        this.groupedReturnFieldOptions = groupBy(this.returnFieldOptions, [{ field: "groupingField" }]) as GroupResult[];
        this.valueOptions = await this.buildValueOptions();

        this.cardsToChange = await this.getSelectedCards();

        this.affectedDeclarations = Object.keys(this.cardsToChange).length;
    }

    private buildReturnFieldOptions(): Option[] {
        let options: Option[];

        if (this.isJobsBillingDashboard) {
            options = [...this.buildJobsAndBillingFieldOptions()];
        } else {
            options = [...this.buildBaseFieldOptions(), ...this.buildGroupFieldOptions(), ...this.buildRoleFieldOptions()];
        }

        return options.sort(this.compareFn());
    }

    private buildBaseFieldOptions(): Option[] {
        const returnStatusOption: Option = { value: "ReturnStatus", label: "BulkChange.ReturnStatus", order: 0, groupingField: "baseFields" };
        const categoryOption: Option = { value: "Category", label: "BulkChange.Category", order: 1, groupingField: "baseFields" };
        const returnDeliveryMethodOptions: Option = {
            value: "DeliveryMethod",
            label: "BulkChange.DeliveryMethod",
            order: 2,
            groupingField: "baseFields",
        };

        return [returnStatusOption, categoryOption, returnDeliveryMethodOptions];
    }

    private buildGroupFieldOptions(): Option[] {
        const options: Option[] = [];
        const canContactsViewAddEdit = this.authService.currentUser.can_contacts_view_add_edit === "True";
        if (!this.isiFirmTaxDashboard || canContactsViewAddEdit) {
            if (this.iFirmGroupsService.getGroup1Enabled()) {
                const group1Option: Option = {
                    value: "Group1",
                    label: this.iFirmGroupsService.groups?.group1,
                    order: 3,
                    groupingField: "groupFields",
                };
                options.push(group1Option);
            }
            if (this.iFirmGroupsService.getGroup2Enabled()) {
                const group2Option: Option = {
                    value: "Group2",
                    label: this.iFirmGroupsService.groups?.group2,
                    order: 4,
                    groupingField: "groupFields",
                };
                options.push(group2Option);
            }
        }
        return options;
    }

    private buildRoleFieldOptions(): Option[] {
        const canEditAssignee = !this.isiFirmTaxDashboard || this.authService.currentUser.can_edit_assignee === "True";

        const roles = Array.from(this.iFirmRolesService.rolesWithUsers().values());

        return roles
            .filter((role) => canEditAssignee || !(role.Id === MANAGER_ROLE_ID || role.Id === PARTNER_ROLE_ID))
            .map((role) => ({
                value: role.Id,
                label: role.Description,
                order: ROLE_OPTIONS_ORDER[role.Id] ?? 10,
                groupingField: role.Id === MANAGER_ROLE_ID || role.Id === PARTNER_ROLE_ID ? "groupFields" : "roleFields",
            }));
    }

    private buildJobsAndBillingFieldOptions(): Option[] {
        const options: Option[] = [];
        if (this.jobsAndBillingCommonService.hasAccess("job_status")) {
            options.push({ value: "JobStatus", label: "JobsAndBilling.JobStatus", order: 0, groupingField: "1" });
        }

        if (this.jobsAndBillingCommonService.hasAccess("job_priority")) {
            options.push({ value: "Priority", label: "JobsAndBilling.Priorities.Priority", order: 1, groupingField: "1" });
        }

        if (this.jobsAndBillingCommonService.hasAccess("currently_responsible")) {
            options.push({ value: "CurrentlyResponsible", label: "JobsAndBilling.AssignedRoles.CurrentlyResponsible", order: 2, groupingField: "2" });
        }

        if (this.jobsAndBillingCommonService.hasAccess("job_owner")) {
            options.push({ value: "JobOwner", label: "JobsAndBilling.AssignedRoles.JobOwner", order: 5, groupingField: "2" });
        }

        if (this.jobsAndBillingCurrentSettings.jobPartnerEnabled && this.jobsAndBillingCommonService.hasAccess("job_partner")) {
            options.push({ value: "JobPartner", label: "JobsAndBilling.AssignedRoles.JobPartner", order: 3, groupingField: "2" });
        }

        if (this.jobsAndBillingCurrentSettings.jobManagerEnabled && this.jobsAndBillingCommonService.hasAccess("job_manager")) {
            options.push({ value: "JobManager", label: "JobsAndBilling.AssignedRoles.JobManager", order: 4, groupingField: "2" });
        }

        if (this.authService.currentUser?.can_contacts_view_add_edit === "True") {
            if (this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.groups?.group1.group1ContactsEnabled) {
                const group1Label = this.iFirmGroupsService.groups?.group1;
                const label =
                    this.currentLanguage === "en-CA"
                        ? `Contact ${group1Label?.["en-CA"] ?? ""}`
                        : group1Label?.[this.currentLanguage] + " de contacts";

                const group1Option: Option = {
                    value: "ContactGroup1",
                    label: label,
                    order: 6,
                    groupingField: "3",
                };
                options.push(group1Option);
            }

            if (this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.groups?.group2.group2ContactsEnabled) {
                const group2Label = this.iFirmGroupsService.groups?.group2;
                const label =
                    this.currentLanguage === "en-CA"
                        ? `Contact ${group2Label?.["en-CA"] ?? ""}`
                        : group2Label?.[this.currentLanguage] + " de contacts";

                const group2Option: Option = {
                    value: "ContactGroup2",
                    label: label,
                    order: 7,
                    groupingField: "3",
                };
                options.push(group2Option);
            }
        }

        if (this.authService.currentUser.can_edit_assignee === "True") {
            options.push({ value: "ContactPartner", label: "JobsAndBilling.Contact.Partner", order: 8, groupingField: "3" });
            options.push({ value: "ContactManager", label: "JobsAndBilling.Contact.Manager", order: 9, groupingField: "3" });
        }

        if (
            this.iFirmGroupsService.getGroup1Enabled() &&
            this.iFirmGroupsService.groups?.group1.group1JobsEnabled &&
            this.jobsAndBillingCommonService.hasAccess("job_group_1")
        ) {
            const group1Label = this.iFirmGroupsService.groups?.group1;
            const label =
                this.currentLanguage === "en-CA" ? `Job ${group1Label?.["en-CA"] ?? ""}` : group1Label?.[this.currentLanguage] + " de mandats";

            const group1Option: Option = {
                value: "JobGroup1",
                label: label,
                order: 10,
                groupingField: "4",
            };
            options.push(group1Option);
        }

        if (
            this.iFirmGroupsService.getGroup2Enabled() &&
            this.iFirmGroupsService.groups?.group2.group2JobsEnabled &&
            this.jobsAndBillingCommonService.hasAccess("job_group_2")
        ) {
            const group2Label = this.iFirmGroupsService.groups?.group2;
            const label =
                this.currentLanguage === "en-CA" ? `Job ${group2Label?.["en-CA"] ?? ""}` : group2Label?.[this.currentLanguage] + " de mandats";

            const group2Option: Option = {
                value: "JobGroup2",
                label: label,
                order: 11,
                groupingField: "4",
            };
            options.push(group2Option);
        }

        if (this.jobsAndBillingCommonService.hasAccess("target_start_date")) {
            options.push({ value: "TargetStartDate", label: "JobsAndBilling.TargetStartDate", order: 12, groupingField: "5" });
        }
        if (this.jobsAndBillingCommonService.hasAccess("target_end_date")) {
            options.push({ value: "TargetEndDate", label: "JobsAndBilling.TargetEndDate", order: 13, groupingField: "5" });
        }

        return options;
    }

    private compareFn(): (
        a: { name?: string; internalName?: string; order?: number },
        b: { name?: string; internalName?: string; order?: number },
    ) => number {
        const collator = new Intl.Collator(this.currentLanguage);
        return (a, b) => {
            if (a.order > b.order) {
                return 1;
            } else if (a.order < b.order) {
                return -1;
            } else if (a.name && b.name) {
                return collator.compare(a.name, b.name);
            } else {
                return collator.compare(a.internalName, b.internalName);
            }
        };
    }

    private async buildValueOptions(): Promise<Map<string, Option[]>> {
        if (this.isJobsBillingDashboard) {
            return await this.buildJobsBillingValueOptions();
        } else {
            return await this.buildT1ValueOptions();
        }
    }

    private async buildT1ValueOptions() {
        const valueOptions = new Map<string, Option[]>();

        valueOptions.set("ReturnStatus", this.getStatusOptions());
        valueOptions.set("Category", await this.getCategoryOptions());
        valueOptions.set("Group1", this.getGroup1Options());
        valueOptions.set("Group2", this.getGroup2Options());
        valueOptions.set(MANAGER_ROLE_ID, this.getOptionsByRole(MANAGER_ROLE_ID));
        valueOptions.set(PARTNER_ROLE_ID, this.getOptionsByRole(PARTNER_ROLE_ID));
        valueOptions.set(PREPARER_ROLE_ID, this.getOptionsByRole(PREPARER_ROLE_ID));
        valueOptions.set(REVIEWER_ROLE_ID, this.getOptionsByRole(REVIEWER_ROLE_ID));
        valueOptions.set(ADMINISTRATION_ROLE_ID, this.getOptionsByRole(ADMINISTRATION_ROLE_ID));
        valueOptions.set("DeliveryMethod", await this.getDeliveryMethodOptions());

        this.returnFieldOptions
            .filter((option) => this.isCustomRole(option.value))
            .forEach((option) => {
                valueOptions.set(option.value, this.getOptionsByRole(option.value));
            });

        return valueOptions;
    }

    private async buildJobsBillingValueOptions() {
        const valueOptions = new Map<string, Option[]>();

        valueOptions.set(
            "JobStatus",
            (await this.statusOptions()).map((status) => ({ value: status.Id, label: status.Name[this.currentLanguage] })),
        );
        valueOptions.set("Priority", this.buildPriorityOptions());
        valueOptions.set("CurrentlyResponsible", this.buildActiveUsersOptions());

        if (this.jobsAndBillingCurrentSettings.jobPartnerEnabled) {
            valueOptions.set("JobPartner", this.getOptionsByRole(PARTNER_ROLE_ID));
        }

        if (this.jobsAndBillingCurrentSettings.jobManagerEnabled) {
            valueOptions.set("JobManager", this.getOptionsByRole(MANAGER_ROLE_ID));
        }

        valueOptions.set("JobOwner", this.buildActiveUsersOptions(false));

        if (this.authService.currentUser.can_contacts_view_add_edit === "True") {
            if (this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.groups?.group1.group1ContactsEnabled) {
                valueOptions.set("ContactGroup1", this.getGroup1Options());
            }

            if (this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.groups?.group2.group2ContactsEnabled) {
                valueOptions.set("ContactGroup2", this.getGroup2Options());
            }
        }

        if (this.authService.currentUser.can_edit_assignee === "True") {
            valueOptions.set("ContactManager", this.getOptionsByRole(MANAGER_ROLE_ID));
            valueOptions.set("ContactPartner", this.getOptionsByRole(PARTNER_ROLE_ID));
        }

        if (this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.groups?.group1.group1JobsEnabled) {
            valueOptions.set("JobGroup1", this.getGroup1Options());
        }

        if (this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.groups?.group2.group2JobsEnabled) {
            valueOptions.set("JobGroup2", this.getGroup2Options());
        }

        valueOptions.set("TargetStartDate", null);
        valueOptions.set("TargetEndDate", null);

        return valueOptions;
    }

    private updateRoleValueOptions(): Map<string, Option[]> {
        const valueOptions = new Map<string, Option[]>();

        valueOptions.set(MANAGER_ROLE_ID, this.getOptionsByRole(MANAGER_ROLE_ID));
        valueOptions.set(PARTNER_ROLE_ID, this.getOptionsByRole(PARTNER_ROLE_ID));
        valueOptions.set(PREPARER_ROLE_ID, this.getOptionsByRole(PREPARER_ROLE_ID));
        valueOptions.set(REVIEWER_ROLE_ID, this.getOptionsByRole(REVIEWER_ROLE_ID));
        valueOptions.set(ADMINISTRATION_ROLE_ID, this.getOptionsByRole(ADMINISTRATION_ROLE_ID));

        this.returnFieldOptions
            .filter((option) => this.isCustomRole(option.value))
            .forEach((option) => {
                this.valueOptions.set(option.value, this.getOptionsByRole(option.value));
            });

        return valueOptions;
    }

    private getStatusOptions(): Option[] {
        return this.mappingOfStatusesService
            .getAllReturnStatuses()
            .filter((status) => status.TaxReturnStatus)
            .map((status) => {
                if (status.TaxReturnStatus) {
                    return {
                        value: status.TaxReturnStatus.Id,
                        label: status.TaxReturnStatus.Name,
                    };
                }
            });
    }

    private async getCategoryOptions(): Promise<Option[]> {
        const categories: Option[] = [{ value: "Unassigned", label: "General.UnassignedFem" }];

        (await this.categoriesService.getCategoriesList()).forEach((category) =>
            categories.push({
                value: category.Id,
                label: category.Name,
            }),
        );

        return categories;
    }

    private getGroup2Options(): Option[] {
        return this.iFirmGroupsService
            .getGroup2Options(false)
            .sort((a, b) => compareByLocalizedNameNoneFirst(a, b, this.currentLanguage))
            .map((option) => ({ value: option.Id.toString(), label: option }));
    }

    private getGroup1Options(): Option[] {
        return this.iFirmGroupsService
            .getGroup1Options(false)
            .sort((a, b) => compareByLocalizedNameNoneFirst(a, b, this.currentLanguage))
            .map((option) => ({ value: option.Id.toString(), label: option }));
    }

    private getOptionsByRole(roleId: string): Option[] {
        let options: Option[] = [{ value: UnassignedUserId, label: "General.None" }];
        const roles = this.iFirmRolesService.rolesWithUsers();

        if (roles.has(roleId)) {
            options = options.concat(
                Array.from(roles.get(roleId)?.Users.values() ?? [])
                    .filter((user) => user.Active && (!this.isJobsBillingDashboard || user.LicenseEnabled))
                    .sort((a, b) => compareByFirstAndLastNames(a, b))
                    .map((user) => {
                        return {
                            value: this.usersService.useiFirmUserId(this.currentDashboard) ? user.IFirmUserId : user.Id,
                            label: user.FullName ?? "",
                        };
                    }),
            );
        }

        return options;
    }

    private async getDeliveryMethodOptions(): Promise<Option[]> {
        let options: Option[];
        if (isiFirmTaxDashboard(this.currentDashboard) && this.currentDashboard.TaxYear >= 2025) {
            options = (await this.iFirmDeliveryMethodsService.getiFirmDeliveryMethods()).map((method) => ({
                label: method as { "en-CA": string; "fr-CA": string },
                value: method.Id.toString(),
            }));
        } else {
            const deliveryMethods = this.deliveryMethodsService.getDeliveryMethodsList(this.currentDashboard);
            const deliveryMethodKeys = Object.keys(deliveryMethods);
            options = Array.isArray(deliveryMethodKeys)
                ? deliveryMethodKeys.map((methodId) => ({
                      label: deliveryMethods[methodId],
                      value: methodId,
                  }))
                : [];
        }

        return options;
    }

    private buildPriorityOptions(): Option[] {
        return this.jobsAndBillingPriorityService.jobPriorityOptions;
    }

    private buildActiveUsersOptions(includeNone = true): Option[] {
        const noneOption: Option = { value: "None", label: "General.None" };
        const users = this.usersService.usersById();
        const userOptions: Option[] = Array.from(users?.fromUserId.values() ?? [])
            .filter((user) => user.Active && user.LicenseEnabled)
            .sort((a, b) => compareByFirstAndLastNames(a, b))
            .map((user) => ({ value: user.IFirmUserId, label: user.FirstName + " " + user.LastName }));

        return includeNone ? [noneOption, ...userOptions] : userOptions;
    }

    private async getJobsAndBillingCurrentSettings() {
        this.jobsAndBillingCurrentSettings = await this.jobsAndBillingCommonService.getCurrentSettings();
    }

    private isCustomRole(value: string): boolean {
        return (
            value !== "ReturnStatus" &&
            value !== "Category" &&
            value !== "Group1" &&
            value !== "Group2" &&
            value !== MANAGER_ROLE_ID &&
            value !== PARTNER_ROLE_ID &&
            value !== PREPARER_ROLE_ID &&
            value !== REVIEWER_ROLE_ID &&
            value !== ADMINISTRATION_ROLE_ID &&
            value !== "DeliveryMethod"
        );
    }

    async getSelectedCards() {
        const cards = UtilsService.recursiveDeepCopy(await this.cardDisplayService.loadAllCardsByIds());
        const selectIds = this.helpersService.selectedCardsIds;
        const selectedCards = {};
        [...cards.entries()]
            .filter(([key, value]) => selectIds.includes(key) && (!this.isiFirmTaxDashboard || this.helpersService.isCardAvailable(key)))
            .reduce((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, selectedCards);
        return selectedCards;
    }

    async ngAfterViewInit() {
        if (!this.helpersService.stepsMerged) {
            await this.helpersService.mergeSteps();
        }
    }

    protected async handleFieldSelectionChange($event) {
        this.selectedReturnField = $event.target.value;
        this.selectedReturnFieldOptions = this.valueOptions?.get(this.selectedReturnField.value) ?? [this.placeholderOption];
        this.selectedValue = this.placeholderOption;

        await this.validateJobFields();
    }

    protected handleValueSelectionChange($event) {
        if (!this.isMultipleJobTypesSelected) {
            this.selectedValue = $event.target.value;
        }
    }

    async applyChanges() {
        if (this.selectedReturnField.value && this.selectedValue.value !== undefined && !this.isUpdating) {
            this.analyticsService.track("A bulk change is performed", {
                UpdatedField: this.selectedReturnField.value,
                UpdatedValue: this.selectedValue.value,
                CardsAffected: this.affectedDeclarations,
            });
            const shouldUpdateiFirmTax = this.shouldUpdateiFirmTax();
            const shouldUpdateContacts = this.shouldUpdateContacts();
            const cardsToChangeIds = Object.keys(this.cardsToChange);
            const cardsToSend = {};
            if (cardsToChangeIds.length > 0) {
                this.isUpdating = true;

                for (const cardId of cardsToChangeIds) {
                    const card: any = { cardId };
                    cardsToSend[cardId] = card;

                    if (this.isJobsBillingDashboard) {
                        if (
                            this.selectedReturnField.value === "ContactGroup1" ||
                            this.selectedReturnField.value === "ContactGroup2" ||
                            this.selectedReturnField.value === "ContactManager" ||
                            this.selectedReturnField.value === "ContactPartner"
                        ) {
                            card["contactInformation"] = {};
                        } else {
                            card["job"] = {};
                        }
                    }

                    switch (this.selectedReturnField.value) {
                        case "Category":
                            if (this.selectedValue.value === "Unassigned") {
                                this.selectedValue.value = null;
                            }
                            card.categoryId = this.selectedValue.value;
                            break;
                        case "ReturnStatus":
                            card.return = {
                                statusId: this.selectedValue.value,
                                statusDate: Math.floor(this.helpersService.convertMillisecondsToSeconds(Date.now())),
                                statusUserId: this.helpersService.getCurrentUser().sub,
                            };
                            break;
                        case "Group1":
                            card.group1Id = this.selectedValue.value?.toString();
                            break;
                        case "Group2":
                            card.group2Id = this.selectedValue.value?.toString();
                            break;
                        case "DeliveryMethod":
                            card.deliveryMethod = Number.parseInt(this.selectedValue.value, 10);
                            break;
                        case "JobStatus":
                            card.job.statusId = this.selectedValue.value?.toString();
                            break;
                        case "Priority":
                            card.job.priority = this.selectedValue.value === null ? null : this.selectedValue.value?.toString();
                            break;
                        case "CurrentlyResponsible":
                            card.job.currentlyResponsible = this.selectedValue.value === "None" ? null : this.selectedValue.value?.toString();
                            break;
                        case "JobPartner":
                            card.job.partner = this.selectedValue.value === UnassignedUserId ? null : this.selectedValue.value?.toString();
                            break;
                        case "JobManager":
                            card.job.manager = this.selectedValue.value === UnassignedUserId ? null : this.selectedValue.value?.toString();
                            break;
                        case "JobOwner":
                            card.job.owner = this.selectedValue.value?.toString();
                            break;
                        case "ContactGroup1":
                            card.contactInformation.group1 = this.selectedValue.value?.toString();
                            break;
                        case "ContactGroup2":
                            card.contactInformation.group2 = this.selectedValue.value?.toString();
                            break;
                        case "ContactManager":
                            card.contactInformation.manager = this.selectedValue.value?.toString();
                            break;
                        case "ContactPartner":
                            card.contactInformation.partner = this.selectedValue.value?.toString();
                            break;
                        case "JobGroup1":
                            card.job.group1 = this.selectedValue.value?.toString();
                            break;
                        case "JobGroup2":
                            card.job.group2 = this.selectedValue.value?.toString();
                            break;
                        case "TargetStartDate":
                            const d = this.selectedValue.value ? new Date(this.selectedValue.value) : null;
                            card.job.targetStartDate = d ? this.helpersService.convertDateOnlyToSeconds(d) : null;
                            break;
                        case "TargetEndDate":
                            const e = this.selectedValue.value ? new Date(this.selectedValue.value) : null;
                            card.job.targetEndDate = e ? this.helpersService.convertDateOnlyToSeconds(e) : null;
                            break;
                        default: {
                            card.roleUserMapping = this.cardsToChange[cardId].roleUserMapping;
                            const i = card.roleUserMapping.findIndex((role) => role.roleId === this.selectedReturnField.value);
                            if (i < 0) {
                                card.roleUserMapping.push({
                                    roleId: this.selectedReturnField.value,
                                    userId: this.selectedValue.value,
                                });
                            } else {
                                card.roleUserMapping[i].userId = this.selectedValue.value;
                            }
                            break;
                        }
                    }
                }

                if (this.isJobsBillingDashboard) {
                    if (
                        this.selectedReturnField.value !== "ContactGroup1" &&
                        this.selectedReturnField.value !== "ContactGroup2" &&
                        this.selectedReturnField.value !== "ContactManager" &&
                        this.selectedReturnField.value !== "ContactPartner"
                    ) {
                        await this.saveBulkJobsChanges();
                    }

                    if (
                        this.selectedReturnField.value === "ContactGroup1" ||
                        this.selectedReturnField.value === "ContactGroup2" ||
                        this.selectedReturnField.value === "ContactManager" ||
                        this.selectedReturnField.value === "ContactPartner"
                    ) {
                        const allContactIds = Object.values(this.cardsToChange).map((card: any) => card.contactInformation?.iFirmContactId);

                        if (allContactIds.length > 0) {
                            for (const contactId of allContactIds) {
                                await this.saveContactChanges(contactId, this.selectedValue.value?.toString());
                            }
                        }
                    }
                }

                let iFirmTaxprepSaveResult = SaveResultEnum.NotApplicable;
                let contactSaveResult = SaveResultEnum.NotApplicable;
                if (shouldUpdateiFirmTax) {
                    try {
                        const response = await this.ifirmTaxDocumentService.updateDocument(this.getiFirmTaxChanges());
                        response.forEach((documentId) => {
                            delete this.cardsToChange[cardsToChangeIds.find((cardId) => this.cardsToChange[cardId].documentId === documentId)];
                        });
                        iFirmTaxprepSaveResult = SaveResultEnum.Success;
                    } catch (err) {
                        iFirmTaxprepSaveResult = SaveResultEnum.Failed;
                        this.isUpdating = false;
                        captureException(err);
                    }
                }
                if (iFirmTaxprepSaveResult != SaveResultEnum.Failed && shouldUpdateContacts) {
                    try {
                        const contactChanges: IFirmContactInformationUpdate[] = this.getContactChanges();
                        if (contactChanges.length > 0) {
                            await this.iFirmContactInformationService.updateContacts(contactChanges);
                            contactSaveResult = SaveResultEnum.Success;
                        }
                    } catch (err) {
                        contactSaveResult = SaveResultEnum.Failed;
                        captureException(err);
                    }
                }
                if (iFirmTaxprepSaveResult == SaveResultEnum.Failed) {
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "UpdateIFirmTaxprepError",
                    });
                } else if (contactSaveResult == SaveResultEnum.Failed) {
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "UpdateContactsError",
                    });
                } else {
                    await this.updateCards(
                        cardsToSend,
                        iFirmTaxprepSaveResult != SaveResultEnum.NotApplicable || contactSaveResult != SaveResultEnum.NotApplicable,
                    );
                }
                this.isUpdating = false;
            }
        }
    }

    private shouldUpdateiFirmTax() {
        return (
            this.isiFirmTaxDashboard &&
            (this.selectedReturnField.value === "ReturnStatus" ||
                this.selectedReturnField.value === PREPARER_ROLE_ID ||
                this.selectedReturnField.value === REVIEWER_ROLE_ID)
        );
    }

    private shouldUpdateContacts() {
        return (
            this.isiFirmTaxDashboard &&
            (this.selectedReturnField.value === "Group1" ||
                this.selectedReturnField.value === "Group2" ||
                this.selectedReturnField.value === PARTNER_ROLE_ID ||
                this.selectedReturnField.value === MANAGER_ROLE_ID ||
                this.selectedReturnField.value === "DeliveryMethod")
        );
    }

    private getiFirmTaxChanges() {
        const changes = [];
        const cardsToChangeIds = Object.keys(this.cardsToChange);
        cardsToChangeIds.forEach((cardId) => {
            const card = this.cardsToChange[cardId];
            const metadata: DashboardUpdateMetadata = {
                documentMetadata: {
                    documentId: card.documentId,
                    returnId: card.return.id,
                },
            };
            if (this.selectedReturnField.value === "ReturnStatus") {
                metadata.documentMetadata.status = this.selectedValue.value;
            }
            if (this.selectedReturnField.value === PREPARER_ROLE_ID) {
                if (!metadata.documentMetadata.assignees) {
                    metadata.documentMetadata.assignees = {};
                }
                metadata.documentMetadata.assignees.preparerUserId = this.usersService.getUserById(this.selectedValue.value)?.IFirmUserId;
            }
            if (this.selectedReturnField.value === REVIEWER_ROLE_ID) {
                if (!metadata.documentMetadata.assignees) {
                    metadata.documentMetadata.assignees = {};
                }
                metadata.documentMetadata.assignees.reviewerUserId = this.usersService.getUserById(this.selectedValue.value)?.IFirmUserId;
            }
            changes.push(metadata);
        });

        return changes;
    }

    private getContactChanges() {
        const changes: IFirmContactInformationUpdate[] = [];
        const fieldToUpdate =
            this.selectedReturnField.value === PARTNER_ROLE_ID
                ? "client_of"
                : this.selectedReturnField.value === MANAGER_ROLE_ID
                  ? "manager_id"
                  : null;
        const cardsToChangeIds = Object.keys(this.cardsToChange);

        cardsToChangeIds.forEach((cardId) => {
            const card = this.cardsToChange[cardId];
            switch (this.selectedReturnField.value) {
                case "Group1": {
                    card.group1Id = this.selectedValue.value?.toString();
                    const contactId = card.contactInformation.iFirmContactId;
                    changes.push({ contactId: Number(contactId), payload: { group_filtering_id1: Number(card.group1Id) }, deliveryMethod: null });
                    break;
                }
                case "Group2": {
                    card.group2Id = this.selectedValue.value?.toString();
                    const contactId = card.contactInformation.iFirmContactId;
                    changes.push({ contactId: Number(contactId), payload: { group_filtering_id2: Number(card.group2Id) }, deliveryMethod: null });
                    break;
                }
                case "DeliveryMethod": {
                    card.deliveryMethod = Number.parseInt(this.selectedValue.value, 10);
                    const contactId = card.contactInformation.iFirmContactId;
                    changes.push({ contactId: Number(contactId), payload: {}, deliveryMethod: card.deliveryMethod.toString() });
                    break;
                }
                default: {
                    card.roleUserMapping = this.cardsToChange[cardId].roleUserMapping;
                    const i = card.roleUserMapping.findIndex((role) => role.roleId === this.selectedReturnField.value);
                    if (i < 0) {
                        card.roleUserMapping.push({
                            roleId: this.selectedReturnField.value,
                            userId: this.selectedValue.value,
                        });
                    } else {
                        card.roleUserMapping[i].userId = this.selectedValue.value;
                    }
                    const contactId = this.cardsToChange[cardId].contactInformation.iFirmContactId;
                    const userId = Number(this.usersService.getUserById(this.selectedValue.value)?.IFirmUserId);
                    changes.push({ contactId: Number(contactId), payload: { [fieldToUpdate]: userId }, deliveryMethod: null });
                    break;
                }
            }
        });
        return changes;
    }

    private async updateCards(cardsToSend, hasIFirmChanges: boolean) {
        this.authService.longOperationEmitter.next(true);
        let retry = 1;
        do {
            cardsToSend = await this.internalUpdateCards(cardsToSend, hasIFirmChanges);
            if (cardsToSend && Object.keys(cardsToSend).length > 0) {
                if (retry > 5) {
                    cardsToSend = null;
                    this.authService.longOperationEmitter.next(false);
                    this.responseErrorHandler.showPopup({
                        typeLayout: "ERROR",
                        messageCode: "429TooManyRequests",
                    });
                } else {
                    await this.delay(this.getRetryBackoffDelay(retry));
                }
            }
            retry++;
        } while (cardsToSend && Object.keys(cardsToSend).length > 0);
    }

    private getRetryBackoffDelay(retry: number): number {
        const baseDelayMs = 1000;
        const maxDelayMs = 30000;
        return Math.min(baseDelayMs * 2 ** (retry - 1), maxDelayMs);
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private async internalUpdateCards(cardsToSend, hasIFirmChanges: boolean) {
        let start = 0;
        let count = Object.keys(cardsToSend).length;
        const cardsToResend: Record<string, unknown> = {};
        while (start < count) {
            const entries = Object.entries(cardsToSend).slice(start, start + this.chunkSize);
            await this.authService.beforeLongOperation(this.chunkSize * 2);
            await lastValueFrom(this.cardService.patchCardBatch(Object.fromEntries(entries)))
                .catch((err) => {
                    this.authService.longOperationEmitter.next(false);
                    count = start;
                    if (err.error && err.error.Message === "Too Many Requests") {
                        this.responseErrorHandler.showPopup({
                            typeLayout: "ERROR",
                            messageCode: "429TooManyRequests",
                        });
                    } else {
                        count = start;
                        // TODO show error message?
                        this.responseErrorHandler.showPopup({
                            typeLayout: "ERROR",
                            messageCode: hasIFirmChanges ? "UpdateTrackingWithIFirmCardError" : "UpdateTrackingCardError",
                        });
                    }
                    captureException(err);
                    return null;
                })
                .then(async (response: PatchCardBatchResponse) => {
                    // Response is null if there was an error, so we need to check for that before processing the response
                    if (response) {
                        const completed = response.Message.filter((it) => it.resultCode === 200).length;
                        const toResend = response.Message.filter((it) => it.resultCode === 429);
                        if (toResend.length > 0) {
                            toResend.forEach((it) => {
                                cardsToResend[it.cardId as string] = cardsToSend[it.cardId as string];
                            });
                        }
                        this.completedUpdateCounter += completed;
                        start += this.chunkSize;
                        if (start >= count) {
                            if (Object.keys(cardsToResend).length == 0) {
                                this.authService.longOperationEmitter.next(false);
                                this.isUpdating = false;
                                this.isDone = true;
                                return null;
                            }
                        } else {
                            return cardsToResend;
                        }
                    }
                });
        }
        return null;
    }

    protected close() {
        if (!this.isUpdating) {
            this.authService.longOperationEmitter.next(false);
            this.closeModal.emit();
        }
    }

    protected handleUserRequest(_: any) {
        this.close();
    }

    public handleKeyDown(pressedKey) {
        if (pressedKey.key === "Enter") {
            return false;
        }
    }

    public handleValueChange(e: any, value: string) {
        const selectedDate = this.datePipe.transform(e.toISOString(), "yyyy-MM-dd HH:mm");
        this.dateValidation(value, selectedDate);
    }

    private dateValidation(value: string, selectedDate: string) {
        const cardsArray = Object.values(this.cardsToChange);
        const selectedDateTimestamp = Math.floor(new Date(selectedDate).getTime() / 1000);

        if (value === "TargetStartDate") {
            const earliestTargetEndDate = cardsArray
                .map((card: any) => card?.job?.targetEndDate)
                .filter((d) => !!d)
                .reduce((min, curr) => (curr < min ? curr : min), Number.POSITIVE_INFINITY);

            if (selectedDateTimestamp <= earliestTargetEndDate) {
                this.selectedValue = { value: selectedDate, label: selectedDate };
                this.invalidTargetStartDate = false;
                this.setDatepicker(selectedDate);
            } else {
                this.invalidTargetStartDate = true;
                this.setDatepicker(null);
            }
        } else if (value === "TargetEndDate") {
            const latestTargetStartDate = cardsArray
                .map((card: any) => card?.job?.targetStartDate)
                .filter((d) => !!d)
                .reduce((max, curr) => (curr > max ? curr : max), Number.NEGATIVE_INFINITY);

            if (selectedDateTimestamp >= latestTargetStartDate) {
                this.selectedValue = { value: selectedDate, label: selectedDate };
                this.invalidTargetEndDate = false;
                this.setDatepicker(selectedDate);
            } else {
                this.invalidTargetEndDate = true;
                this.setDatepicker(null);
            }
        }
    }

    private setDatepicker(selectedDate: string) {
        const dp = this.datepicker();

        if (dp) {
            dp.value = selectedDate !== null ? new Date(selectedDate) : null;
        }
    }

    private async validateJobFields() {
        if (this.isJobsBillingDashboard && this.cardsToChange && Object.keys(this.cardsToChange).length > 0) {
            if (this.selectedReturnField.value === "JobStatus") {
                const typeIds = Object.values(this.cardsToChange).map((card: any) => card?.job?.typeId);
                this.isMultipleJobTypesSelected = new Set(typeIds).size > 1;
            } else if (this.selectedReturnField.value === "TargetStartDate" || this.selectedReturnField.value === "TargetEndDate") {
                this.setDatepicker(null);
                this.invalidTargetEndDate = false;
                this.invalidTargetStartDate = false;
                this.isMultipleJobTypesSelected = false;
            } else {
                this.invalidTargetEndDate = false;
                this.invalidTargetStartDate = false;
                this.isMultipleJobTypesSelected = false;
            }
        }
    }

    async saveBulkJobsChanges(): Promise<SaveResultEnum> {
        try {
            const response = await this.jobsAndBillingJobsService.updateBulkJobs(this.getJobsPayload());

            if (response && response.length > 0) {
                return SaveResultEnum.Failed;
            }
            return SaveResultEnum.Success;
        } catch (err) {
            console.error("jobsAndBillingJobsService.updateBulkJobs error : " + err);
            return SaveResultEnum.Failed;
        }
    }

    async saveContactChanges(iFirmContactId: string, value: string): Promise<SaveResultEnum> {
        const contactChanges: IFirmContactInformationUpdate[] = [];

        const contactChange: IFirmContactInformationUpdate = {
            payload: {},
            contactId: Number(iFirmContactId),
            deliveryMethod: null,
        };

        if (this.cardModalService.canContactsViewAddEdit()) {
            if (this.selectedReturnField.value === "ContactGroup1") {
                contactChange.payload["group_filtering_id1"] = Number(value);
            }

            if (this.selectedReturnField.value === "ContactGroup2") {
                contactChange.payload["group_filtering_id2"] = Number(value);
            }
        }

        if (this.cardModalService.canEditAssignee()) {
            if (this.selectedReturnField.value === "ContactManager") {
                contactChange.payload["manager_id"] = Number(value);
            }

            if (this.selectedReturnField.value === "ContactPartner") {
                contactChange.payload["client_of"] = Number(value);
            }
        }

        if (Object.keys(contactChange.payload).length > 0) {
            contactChanges.push(contactChange);
        }

        if (contactChanges.length > 0) {
            try {
                await this.iFirmContactInformationService.updateContacts(contactChanges);
                return SaveResultEnum.Success;
            } catch (err) {
                console.error("contact error : " + err);
                return SaveResultEnum.Failed;
            }
        }
    }

    private getJobsPayload() {
        const allJobIds: number[] = Object.values(this.cardsToChange)
            .map((card: any) => Number(card.job?.id))
            .filter((id) => Number.isInteger(id));
        let jobsChanges: IJobsBulkJobModel | null = null;
        jobsChanges = { data: { jobIds: allJobIds, patch: {} } } as unknown as IJobsBulkJobModel;

        switch (this.selectedReturnField.value) {
            case "JobStatus":
                jobsChanges.data.patch["jobStatusId"] = this.selectedValue.value?.toString();
                break;
            case "Priority":
                jobsChanges.data.patch["jobPriority"] = this.selectedValue.value === null ? null : this.selectedValue.value?.toString();
                break;
            case "CurrentlyResponsible":
                jobsChanges.data.patch["currentlyResponsible"] = this.selectedValue.value === "None" ? null : this.selectedValue.value?.toString();
                break;
            case "JobPartner":
                jobsChanges.data.patch["jobPartner"] = this.selectedValue.value === UnassignedUserId ? null : this.selectedValue.value?.toString();
                break;
            case "JobManager":
                jobsChanges.data.patch["jobManager"] = this.selectedValue.value === UnassignedUserId ? null : this.selectedValue.value?.toString();
                break;
            case "JobOwner":
                jobsChanges.data.patch["jobOwner"] = this.selectedValue.value?.toString();
                break;
            case "JobGroup1":
                jobsChanges.data.patch["jobGroup1"] = this.selectedValue.value?.toString();
                break;
            case "JobGroup2":
                jobsChanges.data.patch["jobGroup2"] = this.selectedValue.value?.toString();
                break;
            case "TargetStartDate":
                const d = this.selectedValue.value ? new Date(this.selectedValue.value) : null;
                jobsChanges.data.patch["targetStartDate"] = d ? this.helpersService.convertDateOnlyToSeconds(d) : null;
                break;
            case "TargetEndDate":
                const e = this.selectedValue.value ? new Date(this.selectedValue.value) : null;
                jobsChanges.data.patch["targetEndDate"] = e ? this.helpersService.convertDateOnlyToSeconds(e) : null;
                break;
        }
        return jobsChanges;
    }

    private readonly allStatuses = computed<ITaxReturnStatus[]>(() => {
        const allReturnStatuses = this.mappingOfStatusesService.getAllReturnStatuses();
        if (!allReturnStatuses) {
            return [];
        }
        return allReturnStatuses.filter((mergedStatus) => !!mergedStatus?.TaxReturnStatus).map((mergedStatus) => mergedStatus.TaxReturnStatus);
    });

    private readonly statusIdsAssociatedWithJobType = computed(async () => {
        const selectedCards = await this.getSelectedCards();
        const jobTypeId = (Object.values(selectedCards)[0] as any)?.job?.typeId;
        const associations = this.jobsAndBillingJobTypesService.jobRoleStatusAssociation();

        return new Set(associations.filter((association) => association.jobTypeId === jobTypeId).map((association) => association.jobStatusId));
    });

    protected readonly statusOptions = computed(async () => {
        const selectedCards = await this.getSelectedCards();
        const jobStatusId = (Object.values(selectedCards)[0] as any)?.job?.statusId;
        const statuses = this.allStatuses();
        const statusIdsAssociatedWithJobType = await this.statusIdsAssociatedWithJobType();

        if (!jobStatusId || !statuses || !statusIdsAssociatedWithJobType?.size) {
            return [];
        }

        return statuses
            .filter((status) => this.filterStatuses(status, statusIdsAssociatedWithJobType, jobStatusId))
            .map((status) => this.applyInactiveLabel(status, statusIdsAssociatedWithJobType));
    });

    private isStatusMapped(statusId: string, statusIdsAssociatedWithJobType: Set<string>): boolean {
        return !!this.mappingOfStatusesService.getStepFromStatusId(statusId) && statusIdsAssociatedWithJobType.has(statusId);
    }

    private filterStatuses(status: ITaxReturnStatus, statusIdsAssociatedWithJobType: Set<string>, jobStatusId: string): boolean {
        return !!status?.Id && (status.Id === jobStatusId || (status.Active && this.isStatusMapped(status.Id, statusIdsAssociatedWithJobType)));
    }

    private applyInactiveLabel(status: ITaxReturnStatus, statusIdsMappedToJobType: Set<string>): ITaxReturnStatus {
        if (!status.Active || (!!status.Id && !this.isStatusMapped(status.Id, statusIdsMappedToJobType))) {
            return {
                ...status,
                Name: {
                    "fr-CA": this.translate.instant("General.Inactive") + status.Name["fr-CA"],
                    "en-CA": this.translate.instant("General.Inactive") + status.Name["en-CA"],
                },
            };
        }
        return status;
    }
}
